import { apiUrl } from "./config";
import { formatFieldError, translateApiErrorText } from "../shared/apiErrorMessage";

const JOBS_URL = `${apiUrl}/word/export-jobs/`;
const RETRY_STATUSES = new Set([408, 429, 502, 503, 504]);

function jobError(message, code, status = 0, extra = {}) {
  return Object.assign(new Error(message), { code, status, cancelled: false, ...extra });
}
const cancelledError = () => jobError("已取消 Word 匯出。", "cancelled", 0, { cancelled: true });
const deadlineError = () => jobError("Word 匯出等待逾時，已停止等待與下載，請稍後重試。", "export_timeout");

function readableValue(value) {
  if (typeof value === "string") return translateApiErrorText(value);
  if (Array.isArray(value)) return value.map(readableValue).join("、");
  if (value && typeof value === "object") return Object.entries(value)
    .map(([key, nested]) => {
      const messages = Array.isArray(nested) ? nested : [nested];
      const known = messages.every((message) => typeof message === "string")
        && messages.map((message) => formatFieldError(key, message));
      return known && known.every(Boolean) ? known.join("、") : `${key}: ${readableValue(nested)}`;
    }).join("；");
  return value == null ? "" : String(value);
}

function responseError(response, data, rawText) {
  const status = response.status || 0;
  if (status === 401) return jobError("登入已失效，請重新登入後匯出 Word。", "authentication_required", status);
  if (status === 403) return jobError("目前帳號沒有權限匯出這份 Word 文件。", "permission_denied", status);
  const detail = data?.error || data?.detail || data?.message || (data && readableValue(data));
  const plainText = String(rawText || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 180);
  return jobError(readableValue(detail) || (plainText ? `Word 匯出服務回應錯誤（HTTP ${status}）：${plainText}` : `Word 匯出服務暫時無法使用（HTTP ${status}）。`),
    data?.code || "export_request_failed", status, { retryable: RETRY_STATUSES.has(status) });
}

function retryAfterMs(response) {
  const value = response.headers?.get?.("Retry-After");
  if (!value) return null;
  const seconds = Number(value);
  const delay = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(value) - Date.now();
  return Number.isFinite(delay) ? Math.max(0, Math.min(delay, 10000)) : null;
}

// The timeout covers both the headers and body, even if a stalled transport
// does not reject fetch promptly after abort().
async function request(url, { authToken, requestToken, signal, timeoutMs = 20000, method = "GET", body, blob = false }) {
  if (signal?.aborted) throw signal.reason || cancelledError();
  const controller = new AbortController();
  let timer;
  let abort;
  const interruption = new Promise((_, reject) => {
    abort = () => {
      controller.abort();
      reject(signal?.reason || cancelledError());
    };
    signal?.addEventListener("abort", abort, { once: true });
    timer = setTimeout(() => {
      controller.abort();
      reject(jobError("Word 匯出服務連線逾時，請稍後重試。", "request_timeout", 0, { retryable: true }));
    }, timeoutMs);
  });
  try {
    return await Promise.race([interruption, (async () => {
      let response;
      try {
        response = await fetch(url, {
          method, signal: controller.signal,
          headers: { Authorization: `Bearer ${authToken}`, "X-Word-Export-Token": requestToken,
            ...(body === undefined ? {} : { "Content-Type": "application/json" }) },
          ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        });
      } catch (error) {
        if (signal?.aborted) throw signal.reason || cancelledError();
        throw jobError("Word 匯出服務連線暫時中斷，請稍後重試。", "network_error", 0, { retryable: true });
      }
      if (blob && response.ok) return { response, blob: await response.blob() };
      let data = null;
      let rawText = "";
      if (typeof response.text === "function") {
        rawText = await response.text();
        try { data = JSON.parse(rawText); } catch { /* Present a readable HTML/text error below. */ }
      } else if (typeof response.json === "function") {
        try { data = await response.json(); } catch { /* Malformed JSON is an explicit protocol error. */ }
      }
      if (!response.ok) {
        const error = responseError(response, data, rawText);
        error.retryAfterMs = retryAfterMs(response);
        throw error;
      }
      return { response, data };
    })()]);
  } catch (error) {
    if (signal?.aborted) throw signal.reason || cancelledError();
    if (error.code) throw error;
    throw jobError("Word 匯出內容傳輸暫時中斷，請稍後重試。", "network_error", 0, { retryable: true });
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}

function wait(ms, signal) {
  if (signal.aborted) return Promise.reject(signal.reason || cancelledError());
  return new Promise((resolve, reject) => {
    const cleanup = () => { clearTimeout(timer); signal.removeEventListener("abort", abort); };
    const abort = () => { cleanup(); reject(signal.reason || cancelledError()); };
    const timer = setTimeout(() => { cleanup(); resolve(); }, ms);
    signal.addEventListener("abort", abort, { once: true });
  });
}

function newRequestId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (!window.crypto?.getRandomValues) throw jobError("瀏覽器無法建立匯出識別碼，請更新瀏覽器後重試。", "request_id_unavailable");
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export async function cancelWordExportJob({ authToken, jobId, clientRequestId, requestToken = clientRequestId, timeoutMs = 3500 }) {
  // Independent signal: the caller's signal is already aborted at this point.
  return request(jobId ? `${JOBS_URL}${encodeURIComponent(jobId)}/cancel/` : `${JOBS_URL}cancel/`, {
    authToken, requestToken, method: "POST", timeoutMs,
    body: { client_request_id: clientRequestId },
  });
}

function filenameFromResponse(response, fallback) {
  const header = response.headers?.get?.("Content-Disposition") || "";
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded) {
    try { return decodeURIComponent(encoded[1].trim()); } catch { /* Use the ordinary filename below. */ }
  }
  const ordinary = header.match(/filename="?([^";]+)"?/i);
  return ordinary?.[1]?.trim() || fallback || "講義.docx";
}

export async function runWordExportJob({ source, payload = {}, authToken, signal, onProgress,
  clientRequestId, pollInterval = 1000, requestTimeoutMs = 20000, downloadTimeoutMs = 120000, deadlineMs = 15 * 60 * 1000,
  retryBaseMs = 700, maxRetries = 2, cancelTimeoutMs = 3500 }) {
  if (!authToken) throw jobError("請先登入後再匯出 Word。", "authentication_required", 401);
  if (signal?.aborted) throw cancelledError();
  const requestId = clientRequestId || newRequestId();
  const controller = new AbortController();
  let stopReason;
  const cancel = () => { stopReason = cancelledError(); controller.abort(stopReason); };
  signal?.addEventListener("abort", cancel, { once: true });
  const deadline = setTimeout(() => { stopReason = deadlineError(); controller.abort(stopReason); }, deadlineMs);
  let job;
  let createAttempted = false;
  let token = requestId;
  const checkActive = () => { if (controller.signal.aborted) throw stopReason || controller.signal.reason || cancelledError(); };
  const withRetry = async (url, options) => {
    for (let attempt = 0; ; attempt += 1) {
      checkActive();
      try {
        return await request(url, { ...options, authToken, requestToken: token, signal: controller.signal, timeoutMs: options.timeoutMs ?? requestTimeoutMs });
      } catch (error) {
        checkActive();
        if (!error.retryable || attempt >= maxRetries) {
          if (error.status === 503) throw jobError("Word 匯出服務暫時忙碌（HTTP 503），重新連線仍未成功，請稍後按「重試匯出」。", "service_unavailable", 503);
          throw error;
        }
        onProgress?.({ ...job, stage: "reconnecting", message: "服務暫時忙碌，正在重新連線…", retryAttempt: attempt + 1, maxRetries });
        await wait(error.retryAfterMs ?? retryBaseMs * (2 ** attempt), controller.signal);
      }
    }
  };
  const readJob = (data) => {
    const next = data?.job || data;
    if (!next?.id || !["queued", "running", "success", "error", "cancelled"].includes(next.status)) {
      throw jobError("Word 匯出服務回應格式錯誤，請稍後重試。", "invalid_job_response");
    }
    return next;
  };
  try {
    checkActive();
    createAttempted = true;
    const created = await withRetry(JOBS_URL, { method: "POST", body: { ...payload, source, client_request_id: requestId } });
    checkActive();
    job = readJob(created.data);
    token = job.request_token || requestId;
    while (true) {
      checkActive();
      onProgress?.(job);
      checkActive();
      if (job.status === "cancelled") throw cancelledError();
      if (job.status === "error") throw jobError(job.error || job.message || "Word 匯出失敗，請稍後重試。", job.code || "export_failed", job.status_code || 0);
      if (job.status === "success") break;
      await wait(pollInterval, controller.signal);
      const result = await withRetry(`${JOBS_URL}${encodeURIComponent(job.id)}/`, {});
      checkActive();
      job = readJob(result.data);
    }
    onProgress?.({ ...job, stage: "downloading", message: "正在下載 Word 文件…" });
    checkActive();
    const result = await withRetry(`${JOBS_URL}${encodeURIComponent(job.id)}/file/`, { blob: true, timeoutMs: downloadTimeoutMs });
    checkActive();
    return { blob: result.blob, jobId: job.id, requestToken: token,
      filename: filenameFromResponse(result.response, job.filename || payload.filename),
      questionCount: Number(job.question_count ?? result.response.headers?.get?.("X-Question-Count") ?? 0),
      handoutRecordId: job.handout_record_id || result.response.headers?.get?.("X-Handout-Record-ID") || "",
      examRecordId: job.exam_record_id || result.response.headers?.get?.("X-Exam-Record-ID") || "",
    };
  } catch (error) {
    let cancellationUnconfirmed = false;
    const creationRejected = !job && !controller.signal.aborted && error.status >= 400 && error.status < 500 && !RETRY_STATUSES.has(error.status);
    if (createAttempted && !creationRejected && !["success", "error", "cancelled"].includes(job?.status) && ![401, 403].includes(error.status)) {
      try {
        const { data } = await cancelWordExportJob({ authToken, jobId: job?.id, clientRequestId: requestId, requestToken: token, timeoutMs: cancelTimeoutMs });
        if (data?.success === false || (!data?.job && !data?.cancelled)) cancellationUnconfirmed = true;
      } catch { cancellationUnconfirmed = true; }
    }
    const failure = controller.signal.aborted ? stopReason || error : error;
    if (cancellationUnconfirmed) {
      failure.cancellationUnconfirmed = true;
      const warning = "已停止等待與下載；伺服器未確認停止，工作將依逾時限制結束。";
      failure.message = failure.cancelled ? warning : `${failure.message} ${warning}`;
    }
    throw failure;
  } finally {
    clearTimeout(deadline);
    signal?.removeEventListener("abort", cancel);
  }
}


export async function fetchWordLayoutPdf({ authToken, jobId, requestToken, signal }) {
  const result = await request(`${JOBS_URL}${encodeURIComponent(jobId)}/preview/`, {
    authToken, requestToken, signal, blob: true, timeoutMs: 150000,
  });
  return result.blob;
}
